:host {
    --dialog-color: var(--color-background-interaction-secondary-contrast);
    --status-color: var(--color-text-inverted);

    display: none;
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 1001;
    background-color: rgba(0, 0, 0, 0.2);
    overflow: auto;

    .grid {
        @include grid;

        align-items: center;
        height: 100%;
    }

    .dialog-wrapper {
        padding: 16px 0;
    }

    .dialog {
        display: block;
        background-color: var(--color-background-interaction-tertiary-default);
        box-shadow: var(--shadow-large);
    }

    .status {
        font-family: 'Helvetica Now Text Bold', sans-serif;
        font-size: 18px;
        line-height: 48px;
        color: var(--status-color);
        background-color: var(--dialog-color);
        padding: 0 24px;
        border-width: 2px 0;
        display: flex;
        align-items: center;

        .svg {
            width: 24px;
            margin-right: 8px;
        }
    }

    .status-border {
        border-top: 4px solid var(--dialog-color);
    }

    .content {
        position: relative;
        padding: 12px 24px 24px;
    }

    .label {
        @include typo-ws-paragraph;

        color: var(--color-text-secondary);
    }

    .headline-wrapper {
        display: flex;
        column-gap: 8px;
        width: calc(100% - 30px);

        slot {
            flex-shrink: 0;
            display: block;
            position: relative;
            top: -2px;
        }
    }

    h3 {
        @include typo-ws-h3;

        color: var(--color-text-default);
        margin: 0 0 24px;
        display: flex;
        align-items: center;
    }

    button {
        all: unset;
        position: absolute;
        top: 16px;
        right: 20px;
        width: 24px;
        color: var(--color-icon-default);
        cursor: pointer;
        outline-offset: -1px;

        &:focus {
            outline: 2px solid var(--color-border-interaction-focus);
            outline-offset: 2px;
        }
    }

    slot[name='button'] {
        border-top: 1px solid var(--color-border-separator-primary);
        padding: 12px;
        display: flex;
        justify-content: flex-end;
    }
}

:host([type='attention']) {
    --dialog-color: var(--color-background-hint-attention-ansi);
    --status-color: var(--color-text-light);
}

:host([type='warning']) {
    --dialog-color: var(--color-background-hint-warning-default);
    --status-color: $color-brand-iron100;
}

:host([type='warning-ansi']) {
    --dialog-color: var(--color-background-hint-warning-ansi);
    --status-color: $color-brand-iron100;
}

:host([type='error']) {
    --dialog-color: var(--color-background-hint-error);
    --status-color: var(--color-text-light);
}

:host([type='valid']) {
    --dialog-color: var(--color-background-hint-valid);
    --status-color: var(--color-text-light);
}

:host([open]) {
    display: block;
}

:host([size='xs']) {
    .dialog-wrapper {
        grid-area: 1 / 6 / 2 / -6;

        @include mq-1 {
            grid-area: 1 / 3 / 2 / -3;
        }
    }
}

:host([size='s']) {
    .dialog-wrapper {
        grid-area: 1 / 5 / 2 / -5;

        @include mq-1 {
            grid-area: 1 / 3 / 2 / -3;
        }
    }
}

:host([size='m']) {
    .dialog-wrapper {
        grid-area: 1 / 4 / 2 / -4;

        @include mq-2 {
            grid-area: 1 / 4 / 2 / -4;
        }

        @include mq-1 {
            grid-area: 1 / 2 / 2 / -2;
        }
    }
}

:host([size='l']) {
    .dialog-wrapper {
        grid-area: 1 / 3 / 2 / -3;

        @include mq-2 {
            grid-area: 1 / 2 / 2 / -2;
        }
    }
}

::slotted(p) {
    @include typo-ws-paragraph;

    color: var(--color-text-secondary);
    margin: 0;
}

::slotted(sew-button) {
    margin-left: 8px;
}

::slotted(sew-ws-button) {
    margin-left: 20px;
}

::slotted(sew-button:first-of-type),
::slotted(sew-ws-button:first-of-type) {
    margin-left: 0;
}

@mixin button-slot {
    slot[name='button'] {
        display: flex;
        flex-direction: column;
        flex-basis: 100%;
        padding: 8px 24px 24px;
    }
}

@mixin slotted-buttons {
    ::slotted(sew-button),
    ::slotted(sew-ws-button) {
        margin-left: 0;
        margin-top: 16px;
    }

    ::slotted(sew-button):first-of-type,
    ::slotted(sew-ws-button):first-of-type {
        margin-top: 0;
    }
}

:host(.squeezed-buttons) {
    @include button-slot;
    @include slotted-buttons;
}

@include mq-1 {
    @include slotted-buttons;
    :host {
        @include button-slot;
    }
}

// website adaptions
:host(.sew-ws) {
    --dialog-color: #{$color-red};
    --status-color: #{$color-white};

    .label {
        font-size: 12px;
        text-transform: uppercase;
    }

    .headline-wrapper {
        slot {
            top: 1px;
        }
    }

    h3 {
        margin: 0 0 12px;
    }

    .content {
        padding: 60px 40px 24px;

        &.has-headlines {
            padding-top: 30px;
        }
    }

    ::slotted(p) {
        color: $color-grey-01-dark;
    }

    slot[name='button'] {
        border-top: 0;
        padding: 0 40px 40px;
    }
}

:host(.sew-ws[type='blank']) {
    .status-border {
        border-top: 0;
    }
}

@include mq-1 {
    :host(.sew-ws) {
        .content {
            padding: 60px 20px 24px;
        }

        slot[name='button'] {
            padding: 0 20px 32px;
        }
    }
}
